import React from 'react';
import { CheckCircle, XCircle, AlertTriangle, X } from 'lucide-react';

// FECOA-1920. Section names are the exporter's payload keys; they are readable
// enough to leak into an operator-facing screen, but only just, so they get
// proper labels here. Anything unmapped falls back to the raw key rather than
// being hidden, since a new section appearing is itself worth seeing.
const SECTION_LABELS = {
  study: 'Study',
  mfa_settings: 'MFA settings',
  survey: 'Surveys',
  assessment: 'Assessments',
  question: 'Questions',
  layout_template: 'Question layout templates',
  screen_layout_template: 'Screen layout templates',
  assessment_template: 'Assessment templates',
  screen: 'Screens',
  screen_question: 'Screen questions',
  assessment_question: 'Assessment questions',
  navigation_rule: 'Navigation rules',
  scoring_rule: 'Scoring rules',
  form_variant: 'Form variants',
  schedule: 'Schedules',
  schedule_version: 'Schedule versions',
  reminder_template: 'Reminder templates',
  device: 'Devices',
  screen_template: 'Screen templates',
  vpg_question: 'VPG library questions',
};

const STATUS_TEXT = {
  match: 'Matches',
  mismatch: 'Differs from the candidate',
  missing_in_target: 'Missing in this environment',
  unexpected_in_target: 'Present here but not in the candidate',
};

const sectionLabel = (name) => SECTION_LABELS[name] || name;

const RESULT_HEADINGS = {
  match: {
    title: 'Target matches the candidate',
    body: 'Every section re-exported from this environment hashes identically to the candidate that was deployed.',
    classes: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
  },
  mismatch: {
    title: 'Target has drifted from the candidate',
    body: 'At least one section no longer matches what was deployed. The most common cause is a hand edit made in this environment after the deploy.',
    classes: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
  },
  unverifiable: {
    title: 'Nothing could be verified',
    body: 'This deployment cannot be compared against its candidate.',
    classes: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
  },
};

const ResultIcon = ({ result }) => {
  if (result === 'match') return <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />;
  if (result === 'mismatch') return <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />;
  return <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />;
};

const VerificationReportView = ({ verification, onClose }) => {
  if (!verification) return null;

  const heading = RESULT_HEADINGS[verification.result] || RESULT_HEADINGS.unverifiable;
  const sections = verification.sections || [];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <ResultIcon result={verification.result} />
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {heading.title}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Verified {new Date(verification.verified_at).toLocaleString()}
              {verification.verified_by_email ? ` by ${verification.verified_by_email}` : ''}
            </p>
          </div>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close verification report"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className={`border rounded-lg p-4 text-sm text-gray-700 dark:text-gray-300 ${heading.classes}`}>
        <p>{heading.body}</p>
        {verification.detail && (
          <p className="mt-2 text-gray-600 dark:text-gray-400">{verification.detail}</p>
        )}
      </div>

      {sections.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 dark:text-gray-400">
                <th className="py-2 pr-6 font-medium">Section</th>
                <th className="py-2 pr-6 font-medium">Result</th>
                <th className="py-2 font-medium">Content checksum</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sections.map((row) => {
                const passed = row.status === 'match';
                return (
                  <tr key={row.name}>
                    <td className="py-2 pr-6 text-gray-900 dark:text-gray-100">
                      {sectionLabel(row.name)}
                    </td>
                    <td className="py-2 pr-6">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium ${passed ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                        {passed
                          ? <CheckCircle className="w-3.5 h-3.5" />
                          : <XCircle className="w-3.5 h-3.5" />}
                        {STATUS_TEXT[row.status] || row.status}
                      </span>
                    </td>
                    <td className="py-2 font-mono text-xs text-gray-500 dark:text-gray-400">
                      {passed ? (
                        <span>{(row.expected || '').slice(0, 12)}…</span>
                      ) : (
                        <span className="block">
                          <span className="block">
                            candidate: {row.expected ? `${row.expected.slice(0, 12)}…` : 'absent'}
                          </span>
                          <span className="block">
                            here: {row.actual ? `${row.actual.slice(0, 12)}…` : 'absent'}
                          </span>
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {verification.expected_payload_checksum && (
        <p className="text-xs font-mono text-gray-500 dark:text-gray-400 break-all">
          Whole-package checksum — candidate {verification.expected_payload_checksum.slice(0, 16)}…, here{' '}
          {(verification.actual_payload_checksum || '').slice(0, 16)}…
        </p>
      )}
    </div>
  );
};

export default VerificationReportView;
